import React, { useEffect, useState } from 'react';
import { User } from "../typings";
import { Row } from "../components/Flex";
import { useParams } from "react-router-dom";
import { PrimaryButton } from "../components/buttons/Buttons";
import TextArea from "../components/TextArea";
import TextField from "../components/TextField";
import { fetchUser, ownUser, sendInvite, unownUser, updateUser } from "./users-api";
import SelectField from "../components/SelectField";
import { useAuth } from "../auth-context";
import { toast } from "react-toastify";
import { useApi } from "../api-context";

const roles = ['ADMIN', 'MEMBER', 'CONTENT_PROVIDER', 'CONTENT_MANAGER'];

function UserPage() {
    const api = useApi();
    const { data } = useAuth();
    const [user, setUser] = useState<User>();

    const { id } = useParams();

    const loadUser = () => {
        if (!id) {
            return;
        }
        fetchUser(api, Number(id))
            .then(setUser)
            .catch(console.error);
    };

    const onUpdateUser = () => {
        if (!user) {
            return;
        }
        updateUser(api, Number(id), user.role, user.name, user.biography)
            .then(() => toast.success('Saved changes'))
            .catch(() => toast.error('Failed to save changes'));
    };

    const onSendInvite = () => {
        sendInvite(api, Number(id))
            .then(() => toast.success('Invitation sent'))
            .catch(() => toast.error('Failed to send invitation'));
    };

    useEffect(() => {
        loadUser();
    }, []);

    if (!user || data?.memberType !== 'ADMIN') {
        return null;
    }

    const isAlliance = data.organisationType === 'ALLIANCE';

    return (
        <>
            <div className="group-page-header">
                <div className="main-title">
                    Edit user information
                </div>
                <Row gap={8}>
                    <div style={{ flex: 1 }}>
                        <div className="editable-field">
                            {user.email}
                        </div>
                    </div>
                    {
                        !isAlliance ? (
                            <>
                                <PrimaryButton
                                    disabled={user.inviteSent}
                                    label="SEND INVITATION"
                                    style={{ flex: 0, minWidth: '150px' }}
                                    onClick={onSendInvite}
                                />
                                <PrimaryButton
                                    label={user.owned ? 'STOP OWNING USER' : 'OWN USER'}
                                    style={{ flex: 0, minWidth: '150px' }}
                                    onClick={() => {
                                        if (user?.owned) {
                                            unownUser(api, Number(id))
                                                .then(() => {
                                                    loadUser();
                                                    toast.success('Unowned user successfully');
                                                })
                                                .catch((reason) => {
                                                    toast.error('Failed to unown user: ' + reason);
                                                });
                                        } else {
                                            ownUser(api, Number(id))
                                                .then(() => {
                                                    loadUser();
                                                    toast.success('Owned user successfully');
                                                })
                                                .catch((reason) => {
                                                    toast.error('Failed to own user: ' + reason);
                                                });
                                        }
                                    }}
                                />
                            </>
                        ) : <></>
                    }
                    <PrimaryButton
                        label="SAVE"
                        style={{ flex: 0, minWidth: '150px' }}
                        onClick={onUpdateUser}
                    />
                </Row>
            </div>
            <div className="page-container">
                <div className="content-container" style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                        <div className="box" style={{ flex: 1, gap: '12px' }}>
                            <div className="text-field">
                                <div className="label">Account type</div>
                                <div>{user.accountType}</div>
                            </div>
                            <div className="text-field">
                                <div className="label">License</div>
                                <div>{user.license}</div>
                            </div>
                            {
                                !isAlliance ? (
                                    <div className="text-field">
                                        <div className="label">Starting password</div>
                                        <div>{user.defaultPassword || '-'}</div>
                                    </div>
                                ) : <></>
                            }

                        </div>
                        <div className="box" style={{ flex: 3, gap: '12px' }}>
                            {
                                !isAlliance ? (
                                    <>
                                        <TextField
                                            label="Name"
                                            placeholder="Enter name"
                                            value={user.name}
                                            setValue={name => setUser({ ...user, name })}
                                        />
                                        <TextArea
                                            label="Biography"
                                            placeholder="Enter bio"
                                            value={user.biography}
                                            setValue={biography => setUser({ ...user, biography })}
                                        />
                                    </>
                                ) : <></>
                            }
                            <SelectField
                                label="Type"
                                placeholder="Select type"
                                value={user.role}
                                options={roles.map(role => ({ value: role, text: role.replace('_', ' ') }))}
                                setValue={role => setUser({ ...user, role })}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default UserPage;
